import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CompanyManagementService from "src/Services/API/companymanagement.service";
import { isSuperAdmin } from "src/Common/function";
import { setActingTenant } from "src/Common/tenant";
import ConfirmDialog from "src/Common/confirmdialog";
import AddCompanyModal from "./AddCompanyModal";
import AddAdminModal from "./AddAdminModal";

const RESEND_COOLDOWN_MS = 60000;

function CompanyManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [isAddCompany, setIsAddCompany] = useState(false);
  const [adminForCompany, setAdminForCompany] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  // Per-invite resend cooldown started locally: { [inviteId]: epochMs }.
  const [cooldownUntil, setCooldownUntil] = useState({});
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Defend the page against direct URL access — the menu item is already gated,
  // but the route should not render for anyone but a Super Admin.
  useEffect(() => {
    let allowed = false;
    try {
      allowed = isSuperAdmin();
    } catch (e) {
      allowed = false;
    }
    if (!allowed) {
      navigate("/");
      return;
    }
    getCompanies();
  }, []);

  const getCompanies = async () => {
    try {
      const response = await CompanyManagementService.list();
      if (response.status === 200) {
        const rows = (response.data || []).map((item) => ({
          ...item,
          formattedCreatedAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-GB") : "",
        }));
        setCompanies(rows);
      } else {
        toast("Something went wrong!");
      }
    } catch (error) {
      toast(error?.response?.data?.message || "Failed to load tenants");
    }
  };

  const handleAddCompany = (data) => {
    CompanyManagementService.create(data)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast(data.adminEmail ? "Tenant created and administrator invited" : "Tenant created");
          getCompanies();
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        toast(error?.response?.data?.message || "Failed to create tenant");
      });
    setIsAddCompany(false);
  };

  const handleAddAdmin = (data) => {
    CompanyManagementService.addAdmin(data)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast("Administrator invited");
          getCompanies();
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        toast(error?.response?.data?.message || "Failed to invite administrator");
      });
    setAdminForCompany(null);
  };

  const handleDelete = () => {
    const company = companyToDelete;
    setCompanyToDelete(null);
    if (!company) return;
    CompanyManagementService.remove(company.id)
      .then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast("Tenant deleted");
          getCompanies();
        } else {
          toast("Something went wrong");
        }
      })
      .catch((error) => {
        // Blocked when the company still has users/data.
        toast(error?.response?.data?.message || "Failed to delete tenant");
      });
  };

  // Enter a tenant: every API call and asset URL switches to its company, so
  // the normal pages show that tenant's data. Exit via the sidebar banner.
  const enterTenant = (company) => {
    setActingTenant({ identifier: company.identifier, name: company.name });
    navigate("/work");
  };

  // Latest administrator invite for the row (list is newest-first).
  const latestInvite = (company) => company.adminInvites?.[0] || null;

  // Cooldown deadline: the later of the server's last-send time and any local
  // cooldown we just started, so it survives a page reload.
  const getCooldownUntil = (invite) => {
    const fromServer = invite.updatedAt ? new Date(invite.updatedAt).getTime() + RESEND_COOLDOWN_MS : 0;
    const fromLocal = cooldownUntil[invite.id] || 0;
    return Math.max(fromServer, fromLocal);
  };

  const resendInvite = (invite) => {
    if (!invite || resendingId === invite.id) return;
    if (getCooldownUntil(invite) > Date.now()) return;
    setResendingId(invite.id);
    CompanyManagementService.resendInvite(invite.id)
      .then(() => {
        toast("Invitation resent successfully");
        setCooldownUntil((prev) => ({ ...prev, [invite.id]: Date.now() + RESEND_COOLDOWN_MS }));
      })
      .catch((error) => {
        const data = error.response?.data;
        if (error.response?.status === 429 && data?.retryAfter) {
          setCooldownUntil((prev) => ({
            ...prev,
            [invite.id]: Date.now() + data.retryAfter * 1000,
          }));
        }
        toast(data?.message || "Failed to resend invitation");
      })
      .finally(() => {
        setResendingId(null);
      });
  };

  const renderAdminInvite = (company) => {
    const invite = latestInvite(company);
    if (!invite) {
      return <span className="text-[12px] font-OpenSansRegular text-subTitleColor">—</span>;
    }
    if (invite.accepted) {
      return (
        <div className="min-w-0">
          <span className="text-[12px] font-OpenSansSemiBold text-green-600">Accepted</span>
          <p className="text-[11px] font-OpenSansRegular text-subTitleColor truncate">{invite.email}</p>
        </div>
      );
    }
    const cooldownLeft = getCooldownUntil(invite) - now;
    const coolingDown = cooldownLeft > 0;
    return (
      <div className="min-w-0 flex items-center gap-2">
        <div className="min-w-0">
          <span className="text-[12px] font-OpenSansSemiBold text-orange-500">Pending</span>
          <p className="text-[11px] font-OpenSansRegular text-subTitleColor truncate">{invite.email}</p>
        </div>
        <span
          onClick={() => resendInvite(invite)}
          className={`text-[11px] font-OpenSansSemiBold shrink-0 ${
            resendingId === invite.id || coolingDown
              ? "text-gray-400 cursor-not-allowed"
              : "text-blueColor cursor-pointer"
          }`}
        >
          {coolingDown ? `${Math.ceil(cooldownLeft / 1000)}s` : "Resend"}
        </span>
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-backgroundColor p-5 overflow-auto">
      {isAddCompany && (
        <AddCompanyModal onAddClicked={handleAddCompany} onCancelClicked={() => setIsAddCompany(false)} />
      )}
      {adminForCompany && (
        <AddAdminModal
          company={adminForCompany}
          onAddClicked={handleAddAdmin}
          onCancelClicked={() => setAdminForCompany(null)}
        />
      )}
      <ConfirmDialog
        title="DELETE TENANT"
        description={`Delete "${companyToDelete?.name}"? Only tenants with no users can be deleted.`}
        shown={!!companyToDelete}
        onConfirmClicked={handleDelete}
        onCancelClicked={() => setCompanyToDelete(null)}
      />

      <div className="flex items-center justify-end mb-4">
        {/* <p className="text-[18px] font-OpenSansSemiBold text-titleColor">Tenants</p> */}
        <div
          onClick={() => setIsAddCompany(true)}
          className="px-4 h-[30px] flex items-center justify-center cursor-pointer bg-blueColor rounded-[6px]"
        >
          <span className="text-[12px] font-OpenSansSemiBold text-white">+ Tenant</span>
        </div>
      </div>

      <div className="w-full bg-white rounded-[6px] overflow-hidden">
        <div className="grid grid-cols-[1.6fr_1.1fr_0.6fr_0.9fr_1.8fr_1.8fr] gap-2 px-4 py-3 border-b border-borderColor">
          {["Name", "Identifier", "Users", "Created", "Admin Invite", "Actions"].map((h) => (
            <span key={h} className="text-[12px] font-OpenSansSemiBold text-subTitleColor">
              {h}
            </span>
          ))}
        </div>

        {companies.length === 0 && (
          <div className="px-4 py-6 text-[12px] font-OpenSansMedium text-subTitleColor">No tenants yet.</div>
        )}

        {companies.map((company) => (
          <div
            key={company.id}
            className="grid grid-cols-[1.6fr_1.1fr_0.6fr_0.9fr_1.8fr_1.8fr] gap-2 px-4 py-3 border-b border-borderColor items-center"
          >
            <span className="text-[13px] font-OpenSansMedium text-titleColor">{company.name}</span>
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor">{company.identifier}</span>
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor">{company.userCount}</span>
            <span className="text-[13px] font-OpenSansRegular text-subTitleColor">{company.formattedCreatedAt}</span>
            {renderAdminInvite(company)}
            <div className="flex items-center gap-3">
              <span
                onClick={() => enterTenant(company)}
                className="text-[12px] font-OpenSansSemiBold text-white bg-blueColor rounded-[6px] px-3 py-1 cursor-pointer"
              >
                Enter
              </span>
              <span
                onClick={() => setAdminForCompany(company)}
                className="text-[12px] font-OpenSansSemiBold text-blueColor cursor-pointer"
              >
                Add Admin
              </span>
              <span
                onClick={() => setCompanyToDelete(company)}
                className={`text-[12px] font-OpenSansSemiBold cursor-pointer ${
                  company.userCount > 0 ? "text-gray-300 pointer-events-none" : "text-red-500"
                }`}
              >
                Delete
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CompanyManagement;
